import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import TimeDiff from "components/basic/timer/TimeDiff";
import { ProgressSpinner } from "primereact/progressspinner";
import useCopyDataset from "hooks/useCopyDataset";
import { Dropdown } from "primereact/dropdown";
import useTimerThreshold from "hooks/useTimerThreshold";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import { useDisplayTimezone } from "hooks/useTimezone";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { AdvancedProps, ISparkLineHandlerProps, ValueLabel } from "./SparkLineTypes";
import SparkLineDisconnectedLabel from "./SparkLineDisconnectedLabel";
import "./SparkLine.scss";
import DiffValueDisplayer from "../value-change-indicator/DiffValueDisplayer";
import { DataDisplayType, DiffType } from "../value-change-indicator/ValueChangeIndicatorTypes";
import { formatDiff } from "../value-change-indicator/ValueChangeIndicatorUtils";

type SparkLineLabelProps = ISparkLineHandlerProps & {
  valueLabel: ValueLabel;
  isExpanded?: boolean;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
  mappedValue?: string | number;
  advancedProps?: AdvancedProps;
};

const SparkLineLabel: React.FC<SparkLineLabelProps> = ({
  valueLabel,
  isExpanded,
  setValueLabel,
  mappedValue,
  advancedProps,
  ...props
}) => {
  const displayTz = useDisplayTimezone();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { isExpired, resetTimer } = useTimerThreshold(props.maxAge, props.lastValueTime?.toEpochMilli());
  useCopyDataset(isHovered, props.title, props.dataset);

  useEffect(() => {
    resetTimer(props.lastValueTime?.toEpochMilli());
  }, [props.lastValueTime, resetTimer]);

  useEffect(() => {
    if (isExpired) {
      setValueLabel(prev => ({
        ...prev,
        content: <SparkLineDisconnectedLabel isMultipleCharts={props.isMultipleCharts} />,
        textColor: "red",
      }));
    }
  }, [isExpired, setValueLabel, props.isMultipleCharts]);

  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;
  const minValue = Math.min(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const maxValue = Math.max(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const fontSize = props.labelFontSize || "2.5rem";

  const style = {
    fontSize,
    fontWeight: 700,
    whiteSpace: "nowrap",
    lineHeight: "2.5rem",
    paddingLeft: props.isMultipleCharts ? "" : "0.5rem",
    top: props.vertical === "top" ? "0" : "auto",
    right: props.horizontal === "right" ? "0" : "auto",
    background: valueLabel.bgColor,
    textWrap: "wrap",
    overflowWrap: "break-word",
    color: isExpired ? "red" : valueLabel.textColor,
    textAlign: "left",
  } as CSSProperties;

  const chartOptions = advancedProps?.allCharts
    ? Object.entries(advancedProps.allCharts).map(([indexStr, title]) => ({
        label: title,
        value: Number(indexStr),
      }))
    : [];

  const prevValue = useMemo(
    () => props.dataArray[props.dataArray.length - 2] ?? props.dataArray[props.dataArray.length - 1],
    [props.dataArray]
  );

  const currentValue = useMemo(() => props.dataArray[props.dataArray.length - 1], [props.dataArray]);

  const diffValue = useMemo(() => {
    if (prevValue && currentValue) {
      return currentValue - prevValue;
    }
    return 0;
  }, [prevValue, currentValue]);

  return (
    <>
      {props.isMultipleCharts &&
        props.generalProperties?.chartsInOneBox &&
        advancedProps?.allCharts &&
        props.generalProperties.displayTitlesInRow && (
          <div className="flex flex-row-reverse align-items-center justify-content-between">
            <div className="flex align-items-center justify-content-end gap-1 w-6">
              {props.isLoading && (
                <div className="flex justify-content-end">
                  <ProgressSpinner className="label-progress-spinner" />
                </div>
              )}
              {props.generalProperties?.hideValues ? null : (
                <div
                  id={`${id}`}
                  className="sparkline-value"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {!isExpired && mappedValue ? mappedValue : valueLabel.content}
                </div>
              )}
            </div>
            <div className="sparkline-title w-full">
              <div className="flex gap-1 flex-wrap">
                {Object.entries(advancedProps.allCharts).map(([indexStr, title]) => {
                  const index = Number(indexStr);
                  const isActive = props.chartIndex === index;

                  return (
                    <div
                      key={index}
                      onClick={() => advancedProps?.setExpandedChartIndex?.(index)}
                      className={`inline-multiple-charts-title${isActive ? "-active" : ""}`}
                    >
                      {title}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      {props.isMultipleCharts &&
        props.generalProperties?.chartsInOneBox &&
        advancedProps?.allCharts &&
        !props.generalProperties.displayTitlesInRow && (
          <div className="flex flex-row-reverse align-items-center justify-content-between w-full gap-2">
            <div className="flex align-items-center justify-content-end gap-2 w-6">
              {props.isLoading && (
                <div className="flex justify-content-end">
                  <ProgressSpinner className="label-progress-spinner" />
                </div>
              )}
              {props.generalProperties?.hideValues ? null : (
                <div
                  id={id}
                  className="sparkline-value"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {!isExpired && mappedValue ? mappedValue : valueLabel.content}
                </div>
              )}
            </div>
            <div className="w-4">
              <Dropdown
                value={props.chartIndex}
                options={chartOptions}
                onChange={e => advancedProps?.setExpandedChartIndex?.(e.value)}
                className="w-full h-2rem flex align-items-center font-bold"
                placeholder="Select Chart"
                panelClassName="centered-dropdown-panel"
                style={{
                  background: "#f3f5f6",
                }}
                dropdownIcon="pi pi-chevron-down"
              />
            </div>
          </div>
        )}

      {props.isMultipleCharts && !props.generalProperties?.chartsInOneBox && (
        <div className="flex flex-row-reverse align-items-center justify-content-between">
          <div className="flex align-items-center justify-content-end gap-2 w-12">
            {props.generalProperties?.hideValues ? null : (
              <div
                id={`${id}`}
                className="sparkline-value"
                style={!props.isMultipleCharts ? style : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {!isExpired && mappedValue ? mappedValue : valueLabel.content}
              </div>
            )}
            {!isExpanded && !props.generalProperties?.expandAll && <i className="pi pi-angle-down" />}
          </div>
          <div className="sparkline-title w-full">{valueLabel.title ?? "\u00A0"}</div>
        </div>
      )}
      {!props.isMultipleCharts && (
        <div
          id={`${id}`}
          style={style}
          className="flex flex-row justify-content-between align-items-center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {!isExpired && mappedValue ? mappedValue : valueLabel.content}
          {advancedProps?.showValueChangeIndicator && (
            <DiffValueDisplayer
              diffValue={diffValue}
              dataDisplayType={DataDisplayType.LAST_VALUE}
              formattedValue={formatDiff(
                prevValue,
                currentValue,
                props.unit,
                valueLabel.value,
                false,
                DiffType.PERCENTAGE,
                props.decimalPlaces
              )}
              diffValueDecimalPlaces={props.decimalPlaces ?? 0}
              useDiffIndicatorColors={advancedProps.useDiffIndicatorColors}
              fontSize={advancedProps.diffNumFontSize ?? "2.5rem"}
              iconFontSize={advancedProps.diffArrowFontSize ?? "1.8rem"}
            />
          )}
        </div>
      )}
      <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
        {advancedProps?.showValueChangeIndicator && props.lastValueTime && (
          <div>
            <b>{T("word.current-value")}: </b>
            {currentValue.toFixed(props.decimalPlaces ?? 0)},{" "}
            {fmtDatetime(props.lastValueTime, displayTz, DATE_PATTERN_SECONDS)} ({`${T("word.age")}: `}
            <TimeDiff time={props.lastValueTime} />)
          </div>
        )}
        {advancedProps?.showValueChangeIndicator && props.prevValueTime && (
          <div>
            <b>{T("word.previous-value")}: </b>
            {prevValue.toFixed(props.decimalPlaces ?? 0)},{" "}
            {fmtDatetime(props.prevValueTime, displayTz, DATE_PATTERN_SECONDS)} ({`${T("word.age")}: `}
            <TimeDiff time={props.prevValueTime} />)
          </div>
        )}
        {!advancedProps?.showValueChangeIndicator && props.lastValueTime && (
          <div>
            <b>{T("word.current-value")}:</b>{" "}
            {fmtDatetime(props.lastValueTime, displayTz, DATE_PATTERN_SECONDS)} ({`${T("word.age")}: `}
            <TimeDiff time={props.lastValueTime} />)
          </div>
        )}
        <div>
          <b>{`${T("word.min")} ${T("word.value").toLowerCase()}`}:</b> {minValue} {props.unit}
        </div>
        <div>
          <b>{`${T("word.max")} ${T("word.value").toLowerCase()}`}:</b> {maxValue} {props.unit}
        </div>
      </Tooltip>
    </>
  );
};

export default SparkLineLabel;
